import { useCallback, useEffect, useRef, useState, FunctionComponent, MouseEvent } from 'react'
import { get } from 'libraries/lodash'
import cx from 'classnames'
import Divider from '@mui/material/Divider'
import { scrollTo } from 'libraries/front-end-core/helpers/document'
import AccordionHeaderContainer from 'redux/containers/components/accordion/AccordionHeaderContainer'
import { ContentItemContainerProps } from 'redux/containers/components/accordion/ContentItemContainer'
import { dismissPopover } from 'services/OverlayService'

const ContentItem: FunctionComponent<ContentItemProps> = ({
  props,
  state,
  renderContent,
  isLogged,
  highlight,
  deviceType: { isMobile },
  setAccordionHighlight,
  pageId
}) => {
  const { title, id, allContentExpanded, cxNames } = props
  const [expanded, setExpanded] = useState(false)
  const [userHasClicked, setUserHasClicked] = useState(false)
  const dividerBackground = get(cxNames, 'contentHeader.headerBackground', '')
  const scrollToRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    setExpanded((allContentExpanded as boolean) && isLogged)
  }, [allContentExpanded, isLogged])

  useEffect(() => {
    if (highlight !== undefined && highlight !== '' && highlight === '#'.concat(id) && isLogged) {
      setExpanded(true)
      props.setAllContentExpanded(true)
    }
  }, [highlight, setExpanded, id, isLogged])

  useEffect(() => {
    // delay to allow scroll debounce & loadData to finish when user clicks content question link
    if (expanded && highlight !== undefined && highlight !== '' && highlight === '#'.concat(id)) {
      scrollTo({ pageId: pageId ?? '', element: scrollToRef.current, animate: true, useWindowScrollTo: isMobile })
    }
  }, [highlight, expanded, scrollToRef])

  useEffect(() => {
    if (userHasClicked && expanded) {
      scrollTo({ pageId: pageId ?? '', element: scrollToRef.current, animate: true, useWindowScrollTo: isMobile })
    }
    setUserHasClicked(false)
  }, [userHasClicked, expanded])

  useEffect(() => {
    setExpanded(Boolean(state.expanded) && isLogged)
  }, [state.expanded, isLogged, scrollToRef])

  const isHighlighted: boolean = Boolean(state.enableHighlight) && highlight !== undefined && highlight === '#'.concat(id)
  const className: string = cx(
    `${props.className as string} depth-${props.depth as number} bg-white`,
    {
      'highlight-yellow': isHighlighted
    }
  )
  const handleOnClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    dismissPopover(e.target)
    e.preventDefault()
    setExpanded(expanded => !expanded)
    setUserHasClicked(true)
    setAccordionHighlight('')
  }, [setExpanded, setUserHasClicked, setAccordionHighlight])

  const canRenderContent = (): boolean => {
    // Solution to FC-1536: schedules not expandable
    if (!(props.prefix as boolean)) {
      return (!(title as boolean) || expanded)
    }

    if (!(props.parentTitle as boolean) && expanded) {
      return true
    }
    if ((props.parentTitle as boolean) && (title as boolean) && expanded) {
      return true
    }

    return (!(title as boolean) && isLogged) || expanded
  }

  const contentVisible: boolean = canRenderContent()

  return (
    <li
      className={className}
      id={id}
      ref={scrollToRef}
    >
      <div
        className='outline-0'
        onClick={handleOnClick}
      >
        <AccordionHeaderContainer
          {...props}
          {...state}
          {...{ isHighlighted, expanded, handleOnClick }}
          sticky={0}
        />
      </div>
      <div className={cx('ph4 mv0', dividerBackground)}>
        <Divider variant='fullWidth' classes={{ middle: 'mv0 mh8 bt bt--phantom-grey' }} />
      </div>
      {contentVisible && renderContent()}
    </li>
  )
}

interface ContentItemProps extends ContentItemContainerProps {
  props: any // FIX ME
  state: any // FIX ME
  renderContent: () => JSX.Element
}

export default ContentItem
